import { Box, Drawer, IconButton, Typography } from "@mui/material";
import { TemplateStat } from "contexts/Metrics";

import { Template } from "features/Influencer/ContactList/schema";
import { formatEpochTimestamp } from "utils/time";

import { calculateScore, getLatestRubric } from "./helpers";

interface Props {
  open: boolean;
  onClose: () => void;
  templates: Template[];
  statsByTemplateId: { [key: number]: TemplateStat };
  onSelectTemplate: (template: Template) => void;
}

export default function EvaluatorSheet({
  open,
  onClose,
  templates,
  statsByTemplateId,
  onSelectTemplate,
}: Props) {
  const withIds = templates.filter((t) => t.id);
  const scored = withIds.filter((t) => (t.rubrics?.length ?? 0) > 0);
  const notScored = withIds.filter((t) => (t.rubrics?.length ?? 0) === 0);
  const total = withIds.length;
  const progress = total > 0 ? (scored.length / total) * 100 : 0;

  const handleSelect = (template: Template) => {
    onSelectTemplate(template);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: styles.paper }}
    >
      <Box sx={styles.header}>
        <Box sx={styles.headerRow}>
          <Typography sx={styles.title}>Template Evaluator</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#717182" }}>
            <Box
              component="i"
              className="fa-solid fa-xmark"
              sx={{ fontSize: 14 }}
            />
          </IconButton>
        </Box>
        <Typography sx={styles.subtitle}>
          {scored.length} of {total} templates have been scored
        </Typography>
        <Box sx={styles.progressTrack}>
          <Box sx={styles.progressFill(progress)} />
        </Box>
      </Box>

      <Box sx={styles.body}>
        {scored.length > 0 && (
          <Box>
            <Typography sx={styles.sectionLabel}>Scored</Typography>
            <Box sx={styles.cardList}>
              {scored.map((template) => {
                const rubric = getLatestRubric(template);
                const percentage = rubric ? calculateScore(rubric) : 0;
                const color = scoreColor(percentage);
                return (
                  <Box
                    key={template.id}
                    sx={styles.card(false)}
                    onClick={() => handleSelect(template)}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={styles.name}>{template.name}</Typography>
                      <Box sx={styles.status(color)}>
                        <Box
                          component="i"
                          className={
                            percentage >= READY_THRESHOLD
                              ? "fa-solid fa-circle-check"
                              : "fa-solid fa-circle-exclamation"
                          }
                          sx={{ fontSize: 12 }}
                        />
                        {shortScoreLabel(percentage)}
                      </Box>
                    </Box>
                    <Typography sx={styles.score(color)}>
                      {rubric?.userScore}/{rubric?.totalScore}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {notScored.length > 0 && (
          <Box>
            <Typography sx={styles.sectionLabel}>
              Not yet scored · {notScored.length} remaining
            </Typography>
            <Box sx={styles.cardList}>
              {notScored.map((template) => {
                const pitches =
                  (template.id &&
                    statsByTemplateId[template.id]?.outreachCount) ||
                  0;
                return (
                  <Box
                    key={template.id}
                    sx={styles.card(true)}
                    onClick={() => handleSelect(template)}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={styles.name}>{template.name}</Typography>
                      <Typography sx={styles.meta}>
                        {pitches.toLocaleString()} pitches sent
                        {template.updatedAt
                          ? ` · Modified ${formatEpochTimestamp({
                              epochTimestamp: template.updatedAt,
                              addYear: true,
                            })}`
                          : ""}
                      </Typography>
                    </Box>
                    <Box sx={styles.evaluateButton}>Evaluate →</Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

const READY_THRESHOLD = 70;

const scoreColor = (percentage: number) => {
  if (percentage >= READY_THRESHOLD) return "#00a63e";
  if (percentage >= 30) return "#e17100";
  return "#dc2626";
};

const shortScoreLabel = (percentage: number) => {
  if (percentage >= READY_THRESHOLD) return "Ready to send";
  if (percentage >= 30) return "Needs tweaks";
  return "Needs improvement";
};

const styles = {
  paper: {
    width: { xs: "100%", sm: 448 },
    maxWidth: "100%",
  },
  header: {
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: "1px solid rgba(0,0,0,0.1)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#0a0a0a",
    letterSpacing: "-0.45px",
  },
  subtitle: {
    fontSize: 14,
    color: "#717182",
    mt: 0.75,
  },
  progressTrack: {
    mt: 1.5,
    height: 8,
    borderRadius: 999,
    backgroundColor: "#ececf0",
    overflow: "hidden",
  },
  progressFill: (percentage: number) => ({
    height: "100%",
    width: `${percentage}%`,
    backgroundColor: "#00c950",
    borderRadius: 999,
  }),
  body: {
    p: 2.5,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    overflowY: "auto",
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#717182",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    mb: 1.5,
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  },
  card: (dashed: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    p: 2,
    borderRadius: "14px",
    border: "1px solid rgba(0,0,0,0.1)",
    borderStyle: dashed ? "dashed" : "solid",
    backgroundColor: "white",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#fafafa" },
  }),
  name: {
    fontSize: 14,
    fontWeight: 500,
    color: "#0a0a0a",
    letterSpacing: "-0.15px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  status: (color: string) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: 12,
    fontWeight: 500,
    color,
    mt: 0.25,
  }),
  score: (color: string) => ({
    fontSize: 14,
    fontWeight: 700,
    color,
    flexShrink: 0,
    letterSpacing: "-0.15px",
  }),
  meta: {
    fontSize: 12,
    color: "#717182",
    mt: 0.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  evaluateButton: {
    flexShrink: 0,
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: "8px",
    px: 1.25,
    py: 0.5,
    fontSize: 12,
    fontWeight: 500,
    color: "#717182",
    whiteSpace: "nowrap",
  },
};
